<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
    SummaryMetricsPanel,
    UserDeviceStatusDoughnutChart,
    QueueCallGaugeChart,
    QueueCallsTable,
    UserDevicesTable,
    RealtimeReportFilters,
} from '@/views/pages/reports/realtime/components';

import { useFilterStore, useRealtimeReportStore } from '@/stores';
import { RealtimeReportService } from '@/services';
import { toastServiceError } from '@/services/core/notification';
import {
    CallType,
    RealtimeReportServiceGetUserDevicesResult,
    RealtimeReportServiceGetQueueCallsStateResult,
    UserExtensionQuality,
    UserDeviceStatus,
    FilterItem,
} from '@/types';

// i18n and global filter state
const { t } = useI18n();
const filterStore = useFilterStore();
const realtimeStore = useRealtimeReportStore();
const levelFilter = computed(() => filterStore.level);

const queueFilter = computed(() => realtimeStore.queuesIds);
const activeFilters = computed(() => realtimeStore.activeFilters);

// Reactive state
const userDevices = ref<RealtimeReportServiceGetUserDevicesResult[]>([]);
const queueCalls = ref<RealtimeReportServiceGetQueueCallsStateResult[]>([]);
const isLoading = ref(false);
const isRunning = ref(false);

let pollingTimeoutId: number | null = null;

const connectedUserDeviceStatuses = new Set([
    UserDeviceStatus.CONNECTED,
    UserDeviceStatus.DIALING,
    UserDeviceStatus.RING,
    UserDeviceStatus.RINGING,
    UserDeviceStatus.INUSE,
    UserDeviceStatus.IN_PAUSE,
    UserDeviceStatus.IN_CALL,
]);

// Called periodically to update real-time data
const fetchData = async () => {
    try {
        isLoading.value = true;
        const [usersResp, queueResp] = await Promise.all([
            RealtimeReportService.getUserDevices({ params: { queues: queueFilter.value } }),
            RealtimeReportService.getQueueCallsState({
                params: { queues: queueFilter.value },
            }),
        ]);
        userDevices.value = usersResp.data;
        queueCalls.value = queueResp.data;
    } catch (err) {
        toastServiceError(err);
    } finally {
        isLoading.value = false;
    }
};

// Starts polling every 5s — restarts automatically if still running
const startPolling = async () => {
    isRunning.value = true;
    const loop = async () => {
        if (!isRunning.value) return;
        await fetchData();
        if (isRunning.value) pollingTimeoutId = window.setTimeout(loop, 5000);
    };
    loop();
};

// Stops polling on unmount or when filters change
const stopPolling = () => {
    isRunning.value = false;
    if (pollingTimeoutId) clearTimeout(pollingTimeoutId);
};

// Lifecycle: start/stop polling
onMounted(() => {
    startPolling();
});

onUnmounted(() => stopPolling());

// Restart polling on level change — ensures data stays scoped to current filter
watch([levelFilter, activeFilters], () => {
    stopPolling();
    startPolling();
});

// Returns the most common quality level among all connected devices
const calculateMostCommonQuality = (
    devices: RealtimeReportServiceGetUserDevicesResult[],
): UserExtensionQuality => {
    const counts = {
        [UserExtensionQuality.HIGH]: 0,
        [UserExtensionQuality.MEDIUM]: 0,
        [UserExtensionQuality.LOW]: 0,
    };

    for (const d of devices) {
        const q = pingToQuality(d.state?.ping);
        if (q !== UserExtensionQuality.UNAVAILABLE) counts[q]++;
    }

    const priorities = [
        UserExtensionQuality.HIGH,
        UserExtensionQuality.MEDIUM,
        UserExtensionQuality.LOW,
    ];

    let result = UserExtensionQuality.UNAVAILABLE;
    let max = 0;

    for (const quality of priorities) {
        const count = counts[quality as keyof typeof counts] ?? 0;
        if (count > max) {
            max = count;
            result = quality;
        }
    }

    return result;
};

// Normalizes ping value into a quality tier
const pingToQuality = (ping: number | null | undefined): UserExtensionQuality => {
    if (typeof ping !== 'number' || ping < 1) return UserExtensionQuality.UNAVAILABLE;
    if (ping > 205) return UserExtensionQuality.LOW;
    if (ping > 150) return UserExtensionQuality.MEDIUM;
    return UserExtensionQuality.HIGH;
};

// UI metrics panel — derived from current realtime state
const realtimeMetrics = computed(() => [
    { title: t('report.realtime.queueCallsCount'), metric: queueCalls.value.length },
    {
        title: t('report.realtime.inboundCallsInProgress'),
        metric: userDevices.value.filter((d) => d.state?.callType === CallType.INBOUND).length,
    },
    {
        title: t('report.realtime.outboundCallsInProgress'),
        metric: userDevices.value.filter((d) => d.state?.callType === CallType.OUTBOUND).length,
    },
    {
        title: t('report.realtime.offlineDevices'),
        metric: userDevices.value.filter(
            (d) => !connectedUserDeviceStatuses.has(d.state?.status as UserDeviceStatus),
        ).length,
    },
    {
        title: t('report.realtime.averageQuality'),
        metric: t(
            `report.realtime.device.quality.${calculateMostCommonQuality(userDevices.value)}`,
        ),
    },
]);

const handleRemoveFilter = (filter: FilterItem) => {
    realtimeStore.clearFilter(filter);
};
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="flex text-xl font-semibold text-gray-800">
                    {{ $t('report.realtime.title') }}
                    <UiSpinner v-if="isLoading" class="theme-color ml-2 h-6 w-6" />
                </h1>
            </template>
            <template #actions>
                <RealtimeReportFilters />
            </template>
        </UiPageHeader>

        <div
            v-if="activeFilters.length"
            class="mt-4 flex bg-gray-50 px-4 py-4 shadow ring-1 ring-gray-300 sm:p-6 md:rounded"
        >
            <template :key="`${filter.field}_${filter.id}`" v-for="filter in activeFilters">
                <UiBadge removable @remove="handleRemoveFilter(filter)">
                    {{ $t(`report.realtime.filters.activeTag.${filter.field}`) }}:
                    {{ filter.label }}
                </UiBadge>
            </template>
        </div>

        <SummaryMetricsPanel :metrics="realtimeMetrics" />

        <div class="grid grid-cols-3 gap-4">
            <QueueCallGaugeChart
                :queueCalls="queueCalls"
                class="col-span-3 h-[200px] md:col-span-1 md:h-[277px]"
            />
            <QueueCallsTable
                :queueCalls="queueCalls"
                class="col-span-3 h-[200px] md:col-span-2 md:h-[277px]"
            />
        </div>

        <div class="grid grid-cols-3 gap-4">
            <UserDeviceStatusDoughnutChart
                :userDevices="userDevices"
                class="col-span-3 h-[200px] md:col-span-1 md:h-[277px]"
            />
            <UserDevicesTable
                :userDevices="userDevices"
                class="col-span-3 h-[200px] md:col-span-2 md:h-[277px]"
            />
        </div>
    </div>
</template>
